import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  AnalyticsPage,
  PageHeader,
  PageTitle,
  DateRange,
  DateSelector,
  AnalyticsOverview,
  OverviewCard,
  CardIcon,
  CardContent,
  CardValue,
  CardLabel,
  AnalyticsCharts,
  ChartSection,
  SectionHeader,
  SectionTitle,
  ChartControls,
  ChartButton,
  ChartContainer,
  MockChart,
  ChartBars,
  ChartBar,
  BarFill,
  BarLabel,
  BarValue
} from '../../styles/AnalyticsStyles';
import {
  GetVendorKPIs,
  GetVendorListAPI,
  GetVendorOrderAnalytics,
  GetVendorProductAnalytics,
  MyAccounts
} from '../../common/CommonApi';

const currency = (value) => `₹${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const dayLabel = (year, month, day) =>
  new Date(year, month - 1, day).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });

const monthLabel = (year, month) =>
  new Date(year, month - 1).toLocaleString('en-IN', { month: 'short', year: '2-digit' });

const EMPTY_KPIS = {
  totalSales: 0,
  completedOrders: 0,
  averageOrderValue: 0,
  productsSold: 0,
  totalTaxableSales: 0,
  gstIncluded: 0,
  ootfitCommission: 0,
  estimatedSettlement: 0,
  returnsCancellations: { value: 0, orders: 0, quantity: 0 },
  netSalesTrend: []
};

const EMPTY_PAGINATION = { page: 1, limit: 10, total: 0, pages: 1 };

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 16px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 16px 20px;
  margin-bottom: 24px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.06);

  @media (max-width: 640px) {
    align-items: stretch;
    padding: 14px;
    gap: 12px;
  }
`;

const DateField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  @media (max-width: 640px) {
    width: 100%;
  }
`;

const DateFieldLabel = styled.label`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #94a3b8;
`;

const DateInput = styled.input`
  padding: 9px 14px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  font-size: 14px;
  color: #1e293b;
  background: #f8fafc;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
  min-width: 160px;

  @media (max-width: 640px) {
    width: 100%;
    min-width: 0;
  }

  &:hover {
    border-color: #cbd5e1;
  }

  &:focus {
    outline: none;
    border-color: #30D5C8;
    background: white;
    box-shadow: 0 0 0 3px rgba(48, 213, 200, 0.18);
  }

  &::-webkit-calendar-picker-indicator {
    cursor: pointer;
    opacity: 0.55;
    transition: opacity 0.15s ease;
  }

  &:hover::-webkit-calendar-picker-indicator,
  &:focus::-webkit-calendar-picker-indicator {
    opacity: 0.9;
  }
`;

const DateArrow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 39px;
  color: #cbd5e1;
  font-size: 16px;

  @media (max-width: 640px) {
    display: none;
  }
`;

const FilterActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;

  @media (max-width: 640px) {
    width: 100%;
    margin-left: 0;

    button {
      flex: 1;
    }
  }
`;

const ClearButton = styled.button`
  padding: 10px 18px;
  border-radius: 10px;
  border: 1.5px solid #e2e8f0;
  background: white;
  color: #64748b;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: #cbd5e1;
    background: #f8fafc;
    color: #1e293b;
  }
`;

const TableWrap = styled.div`
  overflow-x: auto;
`;

const DataTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 720px;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px 14px;
  background: #f8fafc;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 10px 14px;
  border-top: 1px solid #eef2f6;
  font-size: 13px;
  color: #1e293b;
  white-space: nowrap;
`;

const StatusBadge = styled.span`
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  text-transform: capitalize;
  background: ${({ status }) =>
    status === 'delivered' ? '#dcfce7' : status === 'cancelled' ? '#fee2e2' : '#fef3c7'};
  color: ${({ status }) =>
    status === 'delivered' ? '#166534' : status === 'cancelled' ? '#991b1b' : '#92400e'};
`;

const PaginationBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 12px 14px;
  font-size: 13px;
  color: #64748b;

  @media (max-width: 560px) {
    align-items: stretch;
    flex-wrap: wrap;
    justify-content: space-between;

    span {
      flex-basis: 100%;
      line-height: 1.4;
    }

    button {
      flex: 1;
      min-height: 40px;
    }
  }
`;

const PageButton = styled.button`
  padding: 4px 10px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  background: white;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ApplyButton = styled.button`
  padding: 10px 20px;
  border-radius: 10px;
  border: none;
  background: linear-gradient(135deg, #30D5C8 0%, #20B8AC 100%);
  color: white;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(48, 213, 200, 0.35);
  transition: transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 10px rgba(48, 213, 200, 0.45);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const Analytics = () => {
  const [isVendor, setIsVendor] = useState(false);
  const [loading, setLoading] = useState(true);
  const [vendors, setVendors] = useState([]);
  const [selectedVendorId, setSelectedVendorId] = useState('');
  const [kpis, setKpis] = useState(EMPTY_KPIS);
  const [groupBy, setGroupBy] = useState('month');
  const [draftStartDate, setDraftStartDate] = useState('');
  const [draftEndDate, setDraftEndDate] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [products, setProducts] = useState([]);
  const [productsPagination, setProductsPagination] = useState(EMPTY_PAGINATION);
  const [productsPage, setProductsPage] = useState(1);

  const [orders, setOrders] = useState([]);
  const [ordersPagination, setOrdersPagination] = useState(EMPTY_PAGINATION);
  const [ordersPage, setOrdersPage] = useState(1);

  useEffect(() => {
    init();
  }, []);

  const activeVendorId = isVendor ? undefined : selectedVendorId;
  const canLoad = isVendor || Boolean(selectedVendorId);

  useEffect(() => {
    if (canLoad) fetchKPIs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVendor, selectedVendorId, groupBy, startDate, endDate]);

  useEffect(() => {
    if (canLoad) fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVendor, selectedVendorId, startDate, endDate, productsPage]);

  useEffect(() => {
    if (canLoad) fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVendor, selectedVendorId, startDate, endDate, ordersPage]);

  const init = async () => {
    try {
      const myAccounts = await MyAccounts();
      const vendorAccount = Boolean(myAccounts?.isVendor);
      setIsVendor(vendorAccount);

      if (!vendorAccount) {
        const vendorList = await GetVendorListAPI();
        const list = Array.isArray(vendorList?.data?.data) ? vendorList.data.data : [];
        setVendors(list);
        if (list.length > 0) {
          setSelectedVendorId(list[0]._id);
        } else {
          setLoading(false);
        }
      }
    } catch (error) {
      setLoading(false);
    }
  };

  const fetchKPIs = async () => {
    setLoading(true);
    try {
      const response = await GetVendorKPIs(activeVendorId, startDate, endDate, groupBy);
      const data = response?.data?.data;
      setKpis({
        totalSales: data?.totalSales || 0,
        completedOrders: data?.completedOrders || 0,
        averageOrderValue: data?.averageOrderValue || 0,
        productsSold: data?.productsSold || 0,
        totalTaxableSales: data?.totalTaxableSales || 0,
        gstIncluded: data?.gstIncluded || 0,
        ootfitCommission: data?.ootfitCommission || 0,
        estimatedSettlement: data?.estimatedSettlement || 0,
        returnsCancellations: data?.returnsCancellations || { value: 0, orders: 0, quantity: 0 },
        netSalesTrend: Array.isArray(data?.netSalesTrend) ? data.netSalesTrend : []
      });
    } catch (error) {
      setKpis(EMPTY_KPIS);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await GetVendorProductAnalytics(activeVendorId, startDate, endDate, productsPage, 10);
      const data = response?.data?.data;
      setProducts(Array.isArray(data?.products) ? data.products : []);
      setProductsPagination(data?.pagination || EMPTY_PAGINATION);
    } catch (error) {
      setProducts([]);
      setProductsPagination(EMPTY_PAGINATION);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await GetVendorOrderAnalytics(activeVendorId, startDate, endDate, ordersPage, 10);
      const data = response?.data?.data;
      setOrders(Array.isArray(data?.orders) ? data.orders : []);
      setOrdersPagination(data?.pagination || EMPTY_PAGINATION);
    } catch (error) {
      setOrders([]);
      setOrdersPagination(EMPTY_PAGINATION);
    }
  };

  const handleVendorChange = (e) => {
    setSelectedVendorId(e.target.value);
    setProductsPage(1);
    setOrdersPage(1);
  };

  const applyDates = () => {
    setStartDate(draftStartDate);
    setEndDate(draftEndDate);
    setProductsPage(1);
    setOrdersPage(1);
  };

  const clearDates = () => {
    setDraftStartDate('');
    setDraftEndDate('');
    setStartDate('');
    setEndDate('');
    setProductsPage(1);
    setOrdersPage(1);
  };

  const overviewCards = [
    { icon: '💰', label: 'Total Sales (GMV)', value: currency(kpis.totalSales) },
    { icon: '✅', label: 'Completed Orders', value: kpis.completedOrders },
    { icon: '📊', label: 'Average Order Value', value: currency(kpis.averageOrderValue) },
    { icon: '📦', label: 'Products Sold', value: kpis.productsSold },
    { icon: '🧾', label: 'Total Taxable Sales', value: currency(kpis.totalTaxableSales) },
    { icon: '🏛️', label: 'GST Included', value: currency(kpis.gstIncluded) },
    { icon: '🤝', label: 'OOTFIT Commission', value: currency(kpis.ootfitCommission) },
    { icon: '🏦', label: 'Estimated Settlement', value: currency(kpis.estimatedSettlement) },
    {
      icon: '↩️',
      label: 'Returns / Cancellations',
      value: `${currency(kpis.returnsCancellations.value)} (${kpis.returnsCancellations.orders})`
    }
  ];

  const maxTrendSales = Math.max(1, ...kpis.netSalesTrend.map((item) => item.sales || 0));

  return (
    <AnalyticsPage>
      <PageHeader>
        <PageTitle>Vendor Dashboard – Key Performance Indicators</PageTitle>
        {!isVendor && (
          <DateRange>
            <DateSelector value={selectedVendorId} onChange={handleVendorChange}>
              {vendors.length === 0 && <option value="">No vendors found</option>}
              {vendors.map((vendor) => (
                <option key={vendor._id} value={vendor._id}>
                  {vendor.companyName || vendor.fullname || vendor.email}
                </option>
              ))}
            </DateSelector>
          </DateRange>
        )}
      </PageHeader>

      <FilterBar>
        <DateField>
          <DateFieldLabel htmlFor="analytics-start-date">From</DateFieldLabel>
          <DateInput
            id="analytics-start-date"
            type="date"
            value={draftStartDate}
            max={draftEndDate || undefined}
            onChange={(e) => setDraftStartDate(e.target.value)}
          />
        </DateField>
        <DateArrow>→</DateArrow>
        <DateField>
          <DateFieldLabel htmlFor="analytics-end-date">To</DateFieldLabel>
          <DateInput
            id="analytics-end-date"
            type="date"
            value={draftEndDate}
            min={draftStartDate || undefined}
            onChange={(e) => setDraftEndDate(e.target.value)}
          />
        </DateField>
        <FilterActions>
          {(startDate || endDate) && <ClearButton onClick={clearDates}>Clear</ClearButton>}
          <ApplyButton disabled={!draftStartDate || !draftEndDate} onClick={applyDates}>
            Show Results
          </ApplyButton>
        </FilterActions>
      </FilterBar>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <img src="/assets/loading.gif" alt="loading" />
        </div>
      ) : (
        <>
          <AnalyticsOverview>
            {overviewCards.map((card) => (
              <OverviewCard key={card.label}>
                <CardIcon>{card.icon}</CardIcon>
                <CardContent>
                  <CardValue>{card.value}</CardValue>
                  <CardLabel>{card.label}</CardLabel>
                </CardContent>
              </OverviewCard>
            ))}
          </AnalyticsOverview>

          <AnalyticsCharts>
            <ChartSection style={{ gridColumn: '1 / -1' }}>
              <SectionHeader>
                <SectionTitle>Net Sales Trend</SectionTitle>
                <ChartControls>
                  <ChartButton className={groupBy === 'day' ? 'active' : ''} onClick={() => setGroupBy('day')}>
                    Daily
                  </ChartButton>
                  <ChartButton className={groupBy === 'month' ? 'active' : ''} onClick={() => setGroupBy('month')}>
                    Monthly
                  </ChartButton>
                </ChartControls>
              </SectionHeader>
              <ChartContainer>
                {kpis.netSalesTrend.length > 0 ? (
                  <MockChart>
                    <ChartBars>
                      {kpis.netSalesTrend.map((item) => (
                        <ChartBar key={`${item.year}-${item.month}-${item.day || 0}`}>
                          <BarFill style={{ height: `${((item.sales || 0) / maxTrendSales) * 100}%` }} />
                          <BarLabel>
                            {groupBy === 'day' ? dayLabel(item.year, item.month, item.day) : monthLabel(item.year, item.month)}
                          </BarLabel>
                          <BarValue>{currency(item.sales)}</BarValue>
                        </ChartBar>
                      ))}
                    </ChartBars>
                  </MockChart>
                ) : (
                  <span>No sales data available yet.</span>
                )}
              </ChartContainer>
            </ChartSection>
          </AnalyticsCharts>

          <ChartSection style={{ marginBottom: '30px' }}>
            <SectionHeader>
              <SectionTitle>Product-wise Breakdown</SectionTitle>
            </SectionHeader>
            <TableWrap>
              <DataTable>
                <thead>
                  <tr>
                    <Th>Product</Th>
                    <Th>Qty Sold</Th>
                    <Th>Orders</Th>
                    <Th>Total Sales</Th>
                    <Th>Taxable Sales</Th>
                    <Th>GST Included</Th>
                    <Th>OOTFIT Commission</Th>
                    <Th>Est. Settlement</Th>
                  </tr>
                </thead>
                <tbody>
                  {products.length === 0 ? (
                    <tr>
                      <Td colSpan={8}>No product sales in this period.</Td>
                    </tr>
                  ) : (
                    products.map((product) => (
                      <tr key={product.productId}>
                        <Td>{product.name}</Td>
                        <Td>{product.quantitySold}</Td>
                        <Td>{product.orders}</Td>
                        <Td>{currency(product.totalSales)}</Td>
                        <Td>{currency(product.totalTaxableSales)}</Td>
                        <Td>{currency(product.gstIncluded)}</Td>
                        <Td>{currency(product.ootfitCommission)}</Td>
                        <Td>{currency(product.estimatedSettlement)}</Td>
                      </tr>
                    ))
                  )}
                </tbody>
              </DataTable>
            </TableWrap>
            <PaginationBar>
              <span>
                Page {productsPagination.page} of {productsPagination.pages} ({productsPagination.total} products)
              </span>
              <PageButton disabled={productsPage <= 1} onClick={() => setProductsPage((p) => Math.max(1, p - 1))}>
                Prev
              </PageButton>
              <PageButton
                disabled={productsPage >= productsPagination.pages}
                onClick={() => setProductsPage((p) => Math.min(productsPagination.pages, p + 1))}
              >
                Next
              </PageButton>
            </PaginationBar>
          </ChartSection>

          <ChartSection>
            <SectionHeader>
              <SectionTitle>Order Listing</SectionTitle>
            </SectionHeader>
            <TableWrap>
              <DataTable>
                <thead>
                  <tr>
                    <Th>Product</Th>
                    <Th>Qty</Th>
                    <Th>Price</Th>
                    <Th>Total</Th>
                    <Th>Status</Th>
                    <Th>Date</Th>
                    <Th>Taxable Sales</Th>
                    <Th>GST Included</Th>
                    <Th>Commission</Th>
                    <Th>Settlement</Th>
                  </tr>
                </thead>
                <tbody>
                  {orders.length === 0 ? (
                    <tr>
                      <Td colSpan={10}>No orders in this period.</Td>
                    </tr>
                  ) : (
                    orders.map((order) => (
                      <tr key={order.orderId}>
                        <Td>
                          {order.name}
                          {order.size ? ` / ${order.size}` : ''}
                          {order.color ? ` / ${order.color}` : ''}
                        </Td>
                        <Td>{order.quantity}</Td>
                        <Td>{currency(order.price)}</Td>
                        <Td>{currency(order.totalAmount)}</Td>
                        <Td>
                          <StatusBadge status={order.effectiveStatus}>{order.effectiveStatus}</StatusBadge>
                          {order.isCompleted && order.status !== order.effectiveStatus && (
                            <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '2px' }}>via courier</div>
                          )}
                        </Td>
                        <Td>{new Date(order.createdDate).toLocaleDateString('en-IN')}</Td>
                        <Td>{currency(order.totalTaxableSales)}</Td>
                        <Td>{currency(order.gstIncluded)}</Td>
                        <Td>{currency(order.ootfitCommission)}</Td>
                        <Td>{currency(order.estimatedSettlement)}</Td>
                      </tr>
                    ))
                  )}
                </tbody>
              </DataTable>
            </TableWrap>
            <PaginationBar>
              <span>
                Page {ordersPagination.page} of {ordersPagination.pages} ({ordersPagination.total} orders)
              </span>
              <PageButton disabled={ordersPage <= 1} onClick={() => setOrdersPage((p) => Math.max(1, p - 1))}>
                Prev
              </PageButton>
              <PageButton
                disabled={ordersPage >= ordersPagination.pages}
                onClick={() => setOrdersPage((p) => Math.min(ordersPagination.pages, p + 1))}
              >
                Next
              </PageButton>
            </PaginationBar>
          </ChartSection>
        </>
      )}
    </AnalyticsPage>
  );
};

export default Analytics;
